import { Control, useFieldArray, useFormState, UseFormWatch } from "react-hook-form";
import { PickupZoneFormData } from "@/types/forms/bo/pickupZone.form";
import { PICKUP_DAYS, PickupDay } from "@/types/pickupDay";
import { PickupDayButton } from "./pickupDayButton";
import { PickupTimeRow } from "./pickupTimeRow";
import { usePickupDayOptions } from "@/hooks/useEnumOptions";
import { useTranslation } from "react-i18next";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

type CollectionZonePickupDaysProps = {
  watch: UseFormWatch<PickupZoneFormData>;
  control: Control<PickupZoneFormData>;
};

export const CollectionZonePickupDays = ({ watch, control }: CollectionZonePickupDaysProps) => {
  const { t } = useTranslation("boLogistics");
  const translateMessage = useTranslateMessage();
  const pickupDayOptions = usePickupDayOptions();
  const pickupDaysWatch = watch("pickupDays");
  const { errors } = useFormState({ control, name: "pickupDays" });
  const pickupDaysFieldArray = useFieldArray({ control, name: "pickupDays" });

  const isDaySelected = (day: PickupDay) => pickupDaysWatch.some((el) => el.pickupDay === day);

  const handleDayChange = (day: PickupDay) => {
    if (isDaySelected(day)) {
      pickupDaysFieldArray.remove(pickupDaysWatch.findIndex((el) => el.pickupDay === day));
      return;
    }

    const sortedIndex = pickupDaysWatch.findIndex(
      (el) => PICKUP_DAYS.indexOf(day) < PICKUP_DAYS.indexOf(el.pickupDay),
    );
    const insertAt = sortedIndex === -1 ? pickupDaysFieldArray.fields.length : sortedIndex;

    pickupDaysFieldArray.insert(insertAt, { pickupDay: day, pickupTime: "MORNING" });
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      <span className="text-sm font-medium text-gray-700">
        {t("zoneEditModal.pickupDaysLabel")}
      </span>
      <div className="flex w-full flex-row items-center gap-1.5">
        {pickupDayOptions.map((day) => (
          <PickupDayButton
            key={day.value}
            day={day}
            isAlreadySelected={isDaySelected(day.value as PickupDay)}
            handleChange={() => handleDayChange(day.value as PickupDay)}
          />
        ))}
      </div>
      {errors.pickupDays && (
        <p className="text-xs text-red-500">{translateMessage(errors.pickupDays.message)}</p>
      )}
      {pickupDaysFieldArray.fields.map((field, index) => {
        const dayLabel =
          pickupDayOptions.find((opt) => opt.value === field.pickupDay)?.shorten ?? field.pickupDay;
        return <PickupTimeRow key={field.id} control={control} index={index} dayLabel={dayLabel} />;
      })}
    </div>
  );
};
